/**
 * Driver Support View — Container for admin
 *
 * Displays a driver picker, loads that driver's operational state via the
 * backend /admin/driver-view/{driver_id} endpoint, then renders the
 * existing DriverApp component in readOnly mode.
 *
 * Auth: admin JWT only (require_admin). Moved under the Administration
 * sidebar dropdown, which is admin-only — dispatchers no longer have
 * sidebar/route/API access to this page.
 * Never impersonates the driver.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Card, CardContent, Checkbox, Chip,
  CircularProgress, FormControl, FormControlLabel,
  Grid, MenuItem, Select, Snackbar, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import DashboardLayout from "../../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../../examples/Navbars/DashboardNavbar";
import MDBox from "../../../components/MDBox";
import MDButton from "../../../components/MDButton";
import DriverApp from "../driver-app";
import { getCurrentUser, getCurrentRole } from "../../../data/users";
import { listDrivers } from "../../../api/client";
import { getDriverSupportView, logSupportView } from "../../../api/driverSupportView";

export default function DriverSupportView() {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const userRole = getCurrentRole();

  // pendingDriver = what's chosen in the dropdown; selectedDriver = the driver
  // whose state is actually loaded (and being audit-logged / auto-refreshed).
  // Kept separate so picking a name doesn't immediately start a support
  // session — the dispatcher must explicitly click "Load View".
  const [pendingDriver, setPendingDriver] = useState("");
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [drivers, setDrivers] = useState([]);
  const [supportViewData, setSupportViewData] = useState(null);
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [loadingSupport, setLoadingSupport] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(15000);

  useEffect(() => {
    if (userRole !== "admin") {
      setSnackbar({ severity: "error", message: "Access denied — Driver Support View requires admin role." });
      return;
    }
    if (!currentUser?.token) {
      setSnackbar({ severity: "warning", message: "Please sign in to access Driver Support View." });
      return;
    }

    const fetchDrivers = async () => {
      try {
        setLoadingDrivers(true);
        const res = await listDrivers();
        if (res.ok) {
          const data = await res.json();
          setDrivers(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        setSnackbar({ severity: "error", message: `Error loading drivers: ${err.message}` });
      } finally {
        setLoadingDrivers(false);
      }
    };

    fetchDrivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.token, userRole]);

  const loadSupportView = useCallback(async (driverId) => {
    if (!driverId || !currentUser?.token) {
      setSupportViewData(null);
      return;
    }
    try {
      setLoadingSupport(true);
      const res = await getDriverSupportView(driverId);
      if (res.ok) {
        const data = await res.json();
        setSupportViewData(data);
      }
    } catch (err) {
      setSnackbar({ severity: "error", message: `Error loading support view: ${err.message}` });
    } finally {
      setLoadingSupport(false);
    }
  }, [currentUser?.token]);

  useEffect(() => {
    loadSupportView(selectedDriver);
  }, [selectedDriver, loadSupportView]);

  useEffect(() => {
    if (!autoRefresh || !selectedDriver || !currentUser?.token) return;
    const intervalId = setInterval(async () => {
      try {
        const res = await getDriverSupportView(selectedDriver);
        if (res.ok) {
          const data = await res.json();
          setSupportViewData(data);
        }
      } catch (err) {
        // Silently fail during auto-refresh
      }
    }, refreshInterval);
    return () => clearInterval(intervalId);
  }, [autoRefresh, selectedDriver, refreshInterval, currentUser?.token]);

  useEffect(() => {
    if (!selectedDriver || !currentUser?.token) return;
    logSupportView("start", selectedDriver).catch((err) => {
      console.warn("[SupportView] Audit log write failed:", err.message);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDriver, currentUser?.token]);

  const handleExitSupportView = () => {
    if (selectedDriver && currentUser?.token) {
      logSupportView("end", selectedDriver).catch(() => {});
    }
    setSupportViewData(null);
    setSelectedDriver(null);
    setPendingDriver("");
    navigate("/");
  };

  const handleDriverPick = (driverId) => {
    setPendingDriver(driverId);
  };

  const handleLoadView = () => {
    if (!pendingDriver) return;
    setSelectedDriver(pendingDriver);
  };

  const getDriverName = (driverId) => {
    const driver = drivers.find((d) => d.id === driverId);
    return driver?.name || "Unknown Driver";
  };

  if (!currentUser) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox py={3}>
          <Alert severity="error">Please sign in to access Driver Support View.</Alert>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Persistent Support View Banner */}
        {selectedDriver && (
          <MDBox
            mb={2}
            sx={{
              position: "sticky", top: 8, zIndex: 10,
              bgcolor: "#fff3cd", color: "#856404",
              border: "2px solid #ffc107", borderRadius: 1,
            }}
          >
            <MDBox display="flex" alignItems="center" justifyContent="space-between" px={2} py={1} flexWrap="wrap" gap={1}>
              <MDBox display="flex" alignItems="center" gap={1.5}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  🔒 SUPPORT VIEW
                </Typography>
                <Chip
                  label={`Viewing as: ${getDriverName(selectedDriver)}`}
                  size="small"
                  sx={{ bgcolor: "rgba(255, 193, 7, 0.3)", fontWeight: 600 }}
                />
                {autoRefresh && (
                  <Chip
                    label={`Auto-refresh: ${refreshInterval / 1000}s`}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 1 }}
                  />
                )}
              </MDBox>
              <MDButton
                variant="outlined" color="error" size="small"
                onClick={handleExitSupportView}
                startIcon={<CloseIcon />}
              >
                Exit Support View
              </MDButton>
            </MDBox>
          </MDBox>
        )}

        {/* Driver Selector + Controls */}
        <Card>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant="subtitle2" gutterBottom>
                  Select Driver to Support
                </Typography>
                {loadingDrivers ? (
                  <CircularProgress size={24} />
                ) : (
                  <FormControl fullWidth>
                    <Select
                      value={pendingDriver}
                      onChange={(e) => handleDriverPick(e.target.value)}
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="">— Choose a driver —</MenuItem>
                      {drivers.map((driver) => (
                        <MenuItem key={driver.id} value={driver.id}>
                          {driver.name}
                          {driver.operational_status && (
                            <Chip
                              label={driver.operational_status}
                              size="small"
                              sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
                              color={
                                driver.operational_status === "Offline" ? "default"
                                : driver.operational_status === "Available" ? "success"
                                : "warning"
                              }
                            />
                          )}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="subtitle2" gutterBottom>
                  Auto-Refresh Interval
                </Typography>
                <FormControl fullWidth>
                  <Select
                    value={refreshInterval}
                    onChange={(e) => setRefreshInterval(Number(e.target.value))}
                    disabled={!autoRefresh || !selectedDriver}
                    size="small"
                  >
                    <MenuItem value={5000}>5 seconds</MenuItem>
                    <MenuItem value={10000}>10 seconds</MenuItem>
                    <MenuItem value={15000}>15 seconds</MenuItem>
                    <MenuItem value={30000}>30 seconds</MenuItem>
                    <MenuItem value={60000}>1 minute</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)}
                      disabled={!selectedDriver}
                      size="small"
                    />
                  }
                  label="Auto-refresh every interval"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <MDButton
                  variant="gradient" color="info" fullWidth
                  onClick={handleLoadView}
                  disabled={!pendingDriver || loadingSupport}
                  startIcon={<PlayArrowIcon />}
                >
                  Load View
                </MDButton>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Driver App in readOnly mode */}
        <MDBox mt={2}>
          {!selectedDriver ? (
            <Alert severity="info">
              Select a driver above and click "Load View" to see their current operational state. The
              Driver App will render in read-only mode so you can assist them over the phone.
            </Alert>
          ) : loadingSupport ? (
            <Card>
              <CardContent>
                <MDBox display="flex" justifyContent="center" alignItems="center" p={4}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>Loading {getDriverName(selectedDriver)}'s state...</Typography>
                </MDBox>
              </CardContent>
            </Card>
          ) : supportViewData ? (
            <Card sx={{ overflow: "hidden" }}>
              <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                <DriverApp readOnly={true} supportViewData={supportViewData} />
              </CardContent>
            </Card>
          ) : (
            <Alert severity="warning">No data available for this driver.</Alert>
          )}
        </MDBox>
      </MDBox>

      {/* Snackbar */}
      {snackbar && (
        <Snackbar
          open={!!snackbar}
          autoHideDuration={6000}
          onClose={() => setSnackbar(null)}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(null)} sx={{ width: "100%" }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      )}
    </DashboardLayout>
  );
}
